// Content selection and ContextPack assembly (ARCHITECTURE.md §9.4).
//
// Each selected file is rendered under one of four content modes chosen from
// its relevance score and size:
//
//   full      score >= 0.7 and tokens <= 2000 — entire file content
//   section   score >= 0.4                    — key sections (imports, exports,
//                                                top-level declarations)
//   signature score >= 0.2                    — exported signatures only
//   reference otherwise                        — path/artifact only, no content
//
// The assembled ContextPack carries the rendered entries plus ContextMetadata
// accounting for tokens, files included / excluded / referenced, and the
// artifacts the pack covers.

import type { SymbolInfo } from "../analyzer/types.js";
import type {
  ContentMode,
  ContextEntry,
  ContextPack,
  ContextSection,
} from "./types.js";
import type { BudgetSelection } from "./budget.js";
import { estimateTokens } from "./budget.js";
import type { RankedFile } from "./ranker.js";

// ---------------------------------------------------------------------------
// Symbol filtering — keep only architecturally significant symbols
// ---------------------------------------------------------------------------

/**
 * Filter symbols to only those architecturally significant for symbol-section
 * rendering.  Function-local variables (e.g. `fn.localVar`) and members of
 * function-local types (e.g. `fn.LocalInterface.field`) are excluded.
 *
 * Pass 1 marks exported symbols and top-level names (no dot in the qualified
 * name) as significant.  Pass 2 propagates significance to children whose
 * parentSymbolId references an already-significant symbol — this correctly
 * includes class/interface members while excluding children of function-locals.
 */
function filterSignificantSymbols(symbols: SymbolInfo[]): SymbolInfo[] {
  const significant = new Set<string>();

  for (const s of symbols) {
    if (s.exported) {
      significant.add(s.symbolId);
      continue;
    }
    const qn = s.symbolId.includes("#")
      ? s.symbolId.slice(s.symbolId.indexOf("#") + 1)
      : s.name;
    if (!qn.includes(".")) {
      significant.add(s.symbolId);
    }
  }

  let changed = true;
  while (changed) {
    changed = false;
    for (const s of symbols) {
      if (significant.has(s.symbolId)) continue;
      if (s.parentSymbolId && significant.has(s.parentSymbolId)) {
        significant.add(s.symbolId);
        changed = true;
      }
    }
  }

  return symbols.filter((s) => significant.has(s.symbolId));
}

// ---------------------------------------------------------------------------
// Content-mode thresholds (§9.4)
// ---------------------------------------------------------------------------

export const CONTENT_MODE_THRESHOLDS = {
  full: 0.7,
  section: 0.4,
  signature: 0.2,
  /** Maximum token size for a "full" rendering. */
  fullMaxTokens: 2000,
} as const;

/** Choose the content mode for a file from its score and token size. */
function selectContentMode(relevanceScore: number, tokens: number): ContentMode {
  if (
    relevanceScore >= CONTENT_MODE_THRESHOLDS.full &&
    tokens <= CONTENT_MODE_THRESHOLDS.fullMaxTokens
  ) {
    return "full";
  }
  if (relevanceScore >= CONTENT_MODE_THRESHOLDS.section) return "section";
  if (relevanceScore >= CONTENT_MODE_THRESHOLDS.signature) return "signature";
  return "reference";
}

// ---------------------------------------------------------------------------
// Content rendering
// ---------------------------------------------------------------------------

/**
 * Key sections of a file: import/export statements and top-level declarations
 * with their opening body lines. Only lines at the top level (no leading
 * whitespace, or preceded by `export`) are selected so that local variables
 * inside function bodies are not mistaken for top-level declarations.
 *
 * For multi-line constructs (functions, classes, interfaces) the opening block
 * is included: starting from the declaration line through the next line that
 * increases indentation depth, capped at 6 continuation lines.
 */
function extractSections(content: string): string {
  const lines = content.split("\n");
  const kept: string[] = [];

  const TOP_LEVEL_RE =
    /^(export\s+)?(default\s+)?(async\s+)?(function|class|interface|type|enum|const|let|var)\b/;
  const IMPORT_RE = /^(import|export\s*\{|export\s*\*|export\s+type\s*\{|from)\b/;
  const MAX_CONTINUATION = 6;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.length === 0 || trimmed.startsWith("//") || trimmed.startsWith("/*")) {
      i++;
      continue;
    }

    // Only match lines that start at column 0 (no indentation) to stay
    // at the module top-level.
    const isTopLevel = line.length > 0 && line[0] !== " " && line[0] !== "\t";
    if (!isTopLevel) {
      i++;
      continue;
    }

    if (IMPORT_RE.test(trimmed)) {
      kept.push(line);
      i++;
      continue;
    }

    if (TOP_LEVEL_RE.test(trimmed)) {
      kept.push(line);
      if (!trimmed.endsWith(";") && !trimmed.endsWith(",")) {
        const isDefinitionBlock =
          /^(export\s+)?(default\s+)?(interface|type|enum)\b/.test(trimmed);

        if (isDefinitionBlock && trimmed.endsWith("{")) {
          // Interfaces, types with block body, and enums: capture the full
          // block up to the closing brace so the complete API is visible.
          let depth = 1;
          let extra = 0;
          const MAX_BLOCK = 60;
          while (depth > 0 && extra < MAX_BLOCK && i + 1 + extra < lines.length) {
            const next = lines[i + 1 + extra];
            kept.push(next);
            extra++;
            for (const ch of next) {
              if (ch === "{") depth++;
              else if (ch === "}") depth--;
            }
          }
          i += 1 + extra;
        } else {
          // Functions, classes, variables: include the opening signature
          // (parameters, return type) but stop before body statements.
          const BODY_RE = /^\s*(return|const|let|var|if|for|while|switch|throw|try|await|yield)\b/;
          let extra = 0;
          while (extra < MAX_CONTINUATION && i + 1 + extra < lines.length) {
            const next = lines[i + 1 + extra];
            if (next.length > 0 && next[0] !== " " && next[0] !== "\t") break;
            if (next.trim().length === 0) break;
            if (BODY_RE.test(next)) break;
            kept.push(next);
            extra++;
            if (next.trim().endsWith("{")) break;
          }
          i += 1 + extra;
        }
      } else {
        i++;
      }
      continue;
    }

    i++;
  }

  return kept.join("\n");
}

/** Exported signatures only: lines that begin an `export`. */
function extractSignatures(content: string): string {
  const lines = content.split("\n");
  const kept = lines.filter((line) => /^\s*export\b/.test(line));
  return kept.join("\n");
}

/**
 * Symbol-scoped sections (§6.1): one ContextSection per affected symbol,
 * spanning `symbol.line` … `symbol.endLine`. Large container symbols (classes,
 * etc. that enclose other symbols and span more than 50 lines) are decomposed
 * into a short declaration header plus their child symbols as separate sections.
 * Sections are sorted by start line and made non-overlapping; a section fully
 * contained in a prior one is dropped, and a partial overlap is clipped to
 * begin after the previous section. Line numbers are 1-based and inclusive;
 * end lines are clamped to the file's length.
 */
export function extractSymbolSections(
  fileContent: string,
  symbols: SymbolInfo[],
): ContextSection[] {
  const totalLines = fileContent.split("\n").length;
  const CONTAINER_LINE_THRESHOLD = 50;
  const HEADER_LINES = 5;

  const valid = symbols
    .filter(
      (s) =>
        Number.isFinite(s.line) &&
        Number.isFinite(s.endLine) &&
        s.line >= 1 &&
        s.endLine >= s.line,
    )
    .map((s) => ({
      startLine: s.line,
      endLine: Math.min(s.endLine, totalLines),
      symbolId: s.symbolId,
      symbolName: s.name,
    }))
    .sort((a, b) => a.startLine - b.startLine || a.endLine - b.endLine);

  const containerIndices = new Set<number>();
  for (let i = 0; i < valid.length; i++) {
    const outer = valid[i];
    const span = outer.endLine - outer.startLine + 1;
    if (span <= CONTAINER_LINE_THRESHOLD) continue;
    for (let j = 0; j < valid.length; j++) {
      if (i === j) continue;
      const inner = valid[j];
      if (
        inner.startLine >= outer.startLine &&
        inner.endLine <= outer.endLine
      ) {
        containerIndices.add(i);
        break;
      }
    }
  }

  const decomposed: Array<{
    startLine: number;
    endLine: number;
    symbolId?: string;
    symbolName?: string;
  }> = [];
  for (let i = 0; i < valid.length; i++) {
    if (containerIndices.has(i)) {
      const container = valid[i];
      let firstChildStart = container.endLine;
      for (let j = 0; j < valid.length; j++) {
        if (i === j) continue;
        const inner = valid[j];
        if (
          inner.startLine > container.startLine &&
          inner.endLine <= container.endLine
        ) {
          firstChildStart = Math.min(firstChildStart, inner.startLine);
        }
      }
      const headerEnd = Math.min(
        container.startLine + HEADER_LINES - 1,
        firstChildStart - 1,
      );
      if (headerEnd >= container.startLine) {
        decomposed.push({
          startLine: container.startLine,
          endLine: headerEnd,
          symbolId: container.symbolId,
          symbolName: container.symbolName,
        });
      }
    } else {
      decomposed.push(valid[i]);
    }
  }

  decomposed.sort(
    (a, b) => a.startLine - b.startLine || a.endLine - b.endLine,
  );

  const sections: ContextSection[] = [];
  let lastEnd = 0;
  for (const s of decomposed) {
    if (s.endLine <= lastEnd) continue;
    const startLine = Math.max(s.startLine, lastEnd + 1);
    sections.push({
      startLine,
      endLine: s.endLine,
      symbolId: s.symbolId,
      symbolName: s.symbolName,
    });
    lastEnd = s.endLine;
  }
  return sections;
}

/** Concatenate the line ranges named by `sections` from the file content. */
function renderSymbolSections(
  content: string,
  sections: ContextSection[],
): string {
  const lines = content.split("\n");
  return sections
    .map((s) => lines.slice(s.startLine - 1, s.endLine).join("\n"))
    .join("\n");
}

/**
 * Minimum useful extraction ratio.  When section/signature mode produces
 * fewer non-empty lines than this fraction of the original AND fewer than
 * {@link MIN_EXTRACTED_LINES} absolute lines, the extraction is considered
 * too sparse and the caller should fall back to the full file content.
 */
const MIN_EXTRACTION_RATIO = 0.05;
const MIN_EXTRACTED_LINES = 5;

/** Render the content for a file under the chosen mode. */
function renderContent(mode: ContentMode, content: string): string {
  switch (mode) {
    case "full":
      return content;
    case "section":
      return extractSections(content);
    case "signature":
      return extractSignatures(content);
    case "reference":
      return "";
    case "symbol-section":
      return content;
  }
}

/**
 * Check whether a section/signature extraction produced too little content
 * relative to the original file.  When true the caller should fall back to
 * full-file rendering so the AI agent receives something useful rather than
 * an almost-empty stub.
 */
function isExtractionTooSparse(
  extracted: string,
  original: string,
): boolean {
  const origLines = original.split("\n").length;
  if (origLines === 0) return false;
  const extLines = extracted.split("\n").filter((l) => l.trim().length > 0).length;
  return extLines < MIN_EXTRACTED_LINES && extLines / origLines < MIN_EXTRACTION_RATIO;
}

/** Human-readable reason for including a file at the given mode/depth. */
function buildReason(
  mode: ContentMode,
  depth: number,
  score: number,
  sections?: ContextSection[],
): string {
  const where = depth === 0 ? "directly changed" : `affected at depth ${depth}`;
  let reason = `${where}; relevance ${score.toFixed(2)}; included as ${mode}`;
  if (mode === "symbol-section" && sections && sections.length > 0) {
    const names = sections
      .map((s) => s.symbolName ?? s.symbolId)
      .filter((n): n is string => n !== undefined);
    if (names.length > 0) reason += ` (symbols: ${names.join(", ")})`;
  }
  return reason;
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

export interface AssembleContextPackOptions {
  selection: BudgetSelection;
  fileContents: Map<string, string>;
  rankedFiles: RankedFile[];
  budgetTokens: number;
  taskId?: string;
  /**
   * filePath → affected symbols (§6.1). A file present here is rendered in
   * "symbol-section" mode: only the line ranges of its affected symbols are
   * included, instead of the score-derived full/section/signature rendering.
   */
  affectedSymbols?: Map<string, SymbolInfo[]>;
  /** Contract validations from impact overlay (§8.4). */
  affectedValidations?: string[];
  /** Owner agents from impact overlay (§8.4). */
  affectedAgents?: string[];
  /** Workflows from impact overlay (§8.4). */
  affectedWorkflows?: string[];
}

/**
 * Assemble a ContextPack from a budget selection (ARCHITECTURE.md §9.4).
 * Renders each selected file under its content mode and records the budget /
 * coverage metadata.
 */
export function assembleContextPack(
  options: AssembleContextPackOptions,
): ContextPack {
  const {
    selection,
    fileContents,
    rankedFiles,
    budgetTokens,
    taskId,
    affectedSymbols,
    affectedValidations,
    affectedAgents,
    affectedWorkflows,
  } = options;

  // Index ranked files for artifact / depth lookups during rendering.
  const rankByPath = new Map<string, RankedFile>();
  for (const file of rankedFiles) rankByPath.set(file.path, file);

  const entries: ContextEntry[] = [];
  let totalTokens = 0;
  let filesReferenced = 0;
  const artifactsCovered = new Set<string>();

  for (const sel of selection.selected) {
    const ranked = rankByPath.get(sel.path);
    const depth = ranked?.depth ?? 0;
    const rawContent = fileContents.get(sel.path) ?? "";

    // Symbol-scoped rendering takes priority when the file has affected symbols
    // and at least one usable line range can be extracted (§6.1); otherwise the
    // score/size-derived mode applies (backward compatible).
    const rawSymbols = affectedSymbols?.get(sel.path);
    const symbols = rawSymbols ? filterSignificantSymbols(rawSymbols) : undefined;
    let mode: ContentMode;
    let content: string;
    let sections: ContextSection[] | undefined;
    if (symbols && symbols.length > 0 && rawContent.length > 0) {
      const extracted = extractSymbolSections(rawContent, symbols);
      if (extracted.length > 0) {
        mode = "symbol-section";
        sections = extracted;
        content = renderSymbolSections(rawContent, extracted);
      } else {
        mode = selectContentMode(sel.relevanceScore, sel.tokens);
        content = renderContent(mode, rawContent);
      }
    } else {
      mode = selectContentMode(sel.relevanceScore, sel.tokens);
      content = renderContent(mode, rawContent);
    }

    if (
      (mode === "section" || mode === "signature") &&
      isExtractionTooSparse(content, rawContent)
    ) {
      mode = "full";
      content = rawContent;
    }

    totalTokens += estimateTokens(content);
    if (mode === "reference") filesReferenced += 1;
    if (ranked?.artifactId) artifactsCovered.add(ranked.artifactId);

    entries.push({
      path: sel.path,
      ...(ranked?.artifactId ? { artifactId: ranked.artifactId } : {}),
      relevanceScore: sel.relevanceScore,
      contentMode: mode,
      content,
      ...(sections ? { sections } : {}),
      reason: buildReason(mode, depth, sel.relevanceScore, sections),
    });
  }

  const filesIncluded = entries.length - filesReferenced;

  return {
    ...(taskId ? { taskId } : {}),
    entries,
    metadata: {
      totalTokens,
      budgetTokens,
      filesIncluded,
      filesExcluded: selection.excluded.length,
      filesReferenced,
      artifactsCovered: [...artifactsCovered].sort(),
      ...(affectedValidations !== undefined
        ? { affectedValidations }
        : {}),
      ...(affectedAgents !== undefined ? { affectedAgents } : {}),
      ...(affectedWorkflows !== undefined ? { affectedWorkflows } : {}),
      generatedAt: new Date().toISOString(),
    },
  };
}



